import React, { useState, useRef, useEffect } from 'react';

import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Alert, AlertDescription } from './ui/alert';
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Calculator, Loader2, TrendingUp, TrendingDown, Info, Users, AlertCircle } from 'lucide-react';
import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import { get } from 'aws-amplify/api';
import awsconfig from '../aws-exports';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Header } from './Header';

Amplify.configure(awsconfig);

const EnhancedValueDashboard = () => {

    const location = useLocation();
    const navigate = useNavigate();

    const [ticker, setTicker] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeSection, setActiveSection] = useState('overview');
    const [isAuthenticated, setIsAuthenticated] = useState(false);


    useEffect(() => {
        checkAuthStatus();
    }, []);

    const checkAuthStatus = async () => {
        try {
            const session = await fetchAuthSession();
            setIsAuthenticated(!!session.tokens);
            console.log('Auth status:', !!session.tokens); // Debug log
        } catch (error) {
            console.log('No active session');
            setIsAuthenticated(false);
        }
    };

    const [sensitivityParams, setSensitivityParams] = useState({
        wacc: 8.0,
        terminalGrowth: 2.0,
        growthRate: 5.0
    });

    // Initial analysis state with null values
    // Initialize the analysis state with the data passed through navigation
    const [analysis, setAnalysis] = useState(location.state?.data ? {
        companyInfo: {
            name: location.state.data.companyInfo.name,
            ticker: location.state.data.companyInfo.ticker,
            sector: location.state.data.companyInfo.sector,
            industry: location.state.data.companyInfo.industry,
            description: location.state.data.companyInfo.description
        },
        fcfAnalysis: location.state.data.fcfAnalysis,
        valuation: {
            fairValue: location.state.data.valuation.fairValue,
            currentPrice: location.state.data.valuation.currentPrice,
            marginOfSafety: location.state.data.valuation.marginOfSafety,
            enterpriseValue: location.state.data.valuation.enterpriseValue,
            growthRate: location.state.data.valuation.growthRate,
            wacc: location.state.data.valuation.wacc,
            terminalGrowth: location.state.data.valuation.terminalGrowth,
            sharesOutstanding: location.state.data.valuation.sharesOutstanding
        },
        analystThesis: location.state.data.analystThesis || ''
    } : null);

    const handleSaveAnalysis = async () => {
        // TODO: Implement save functionality
        console.log('Saving analysis...');
    };

    const calculateValuation = (growthRates, customWacc = null, customTerminalGrowth = null) => {
        const baseFcf = analysis.fcfAnalysis[0].fcf;
        // Use customWacc if provided, otherwise use from sensitivityParams
        const wacc = (customWacc !== null ? customWacc : sensitivityParams.wacc) / 100;
        // Use customTerminalGrowth if provided, otherwise use from sensitivityParams
        const terminalGrowth = (customTerminalGrowth !== null ? customTerminalGrowth : sensitivityParams.terminalGrowth) / 100;

        // Calculate present value of projected cash flows
        let totalPV = 0;
        let projectedFcf = baseFcf;

        // Calculate each year's FCF and PV
        for (let year = 0; year < 4; year++) {
            const growth = parseFloat(growthRates[year]) / 100;
            projectedFcf *= (1 + growth);
            const discountFactor = Math.pow(1 + wacc, year + 1);
            const pv = projectedFcf / discountFactor;
            totalPV += pv;
        }

        // Calculate terminal value using the last projected FCF and custom terminal growth if provided
        const terminalFcf = projectedFcf * (1 + terminalGrowth);
        const terminalValue = terminalFcf / (wacc - terminalGrowth);
        const discountedTerminalValue = terminalValue / Math.pow(1 + wacc, 4);

        const enterpriseValue = totalPV + discountedTerminalValue;
        const fairValue = enterpriseValue / (analysis.valuation.sharesOutstanding);

        return {
            enterpriseValue,
            fairValue
        };
    };

    // Modified handle WACC change
    const handleWACCChange = (value) => {
        const newWACC = parseFloat(value) || 0;
        // Calculate new valuation with updated WACC
        const newValuation = calculateValuation(yearlyGrowthRates, newWACC);

        // Batch the state updates together
        setSensitivityParams(prev => ({
            ...prev,
            wacc: newWACC
        }));

        setAnalysis(prev => ({
            ...prev,
            valuation: {
                ...prev.valuation,
                enterpriseValue: newValuation.enterpriseValue,
                fairValue: newValuation.fairValue
            }
        }));
    };


    // Modified handle Terminal Growth change
    const handleTerminalGrowthChange = (value) => {
        const newTerminalGrowth = parseFloat(value) || 0;

        // Calculate new valuation with current WACC and new terminal growth
        const newValuation = calculateValuation(yearlyGrowthRates, null, newTerminalGrowth);

        // Batch the state updates together
        setSensitivityParams(prev => ({
            ...prev,
            terminalGrowth: newTerminalGrowth
        }));

        setAnalysis(prev => ({
            ...prev,
            valuation: {
                ...prev.valuation,
                enterpriseValue: newValuation.enterpriseValue,
                fairValue: newValuation.fairValue
            }
        }));
    };

    const handleGrowthRateChange = (year, value) => {
        const newGrowthRates = [...yearlyGrowthRates];
        newGrowthRates[year - 1] = parseFloat(value) || 0;
        setYearlyGrowthRates(newGrowthRates);

        // Recalculate valuation with new growth rates
        const newValuation = calculateValuation(newGrowthRates);

        setAnalysis(prev => ({
            ...prev,
            valuation: {
                ...prev.valuation,
                enterpriseValue: newValuation.enterpriseValue,
                fairValue: newValuation.fairValue
            }
        }));
    };

    const [yearlyGrowthRates, setYearlyGrowthRates] = useState([]);


    const handleAnalyze = async () => {
        if (!ticker) {
            setError('Please enter a stock ticker');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await get({
                apiName: 'dcf',
                path: `/stockdata/${ticker}`
            }).response;

            const reader = response.body.getReader();
            const { value } = await reader.read();
            const text = new TextDecoder().decode(value);
            const data = JSON.parse(text);

            setAnalysis({
                companyInfo: {
                    name: data.companyInfo.name,
                    ticker: data.companyInfo.ticker,
                    sector: data.companyInfo.sector,
                    industry: data.companyInfo.industry,
                    description: data.companyInfo.description
                },
                fcfAnalysis: data.fcfAnalysis,
                valuation: {
                    fairValue: data.valuation.fairValue,
                    currentPrice: data.valuation.currentPrice,
                    marginOfSafety: data.valuation.marginOfSafety,
                    enterpriseValue: data.valuation.enterpriseValue,
                    growthRate: data.valuation.growthRate,
                    wacc: data.valuation.wacc,
                    terminalGrowth: data.valuation.terminalGrowth,
                    sharesOutstanding: data.valuation.sharesOutstanding
                }
            });

            setSensitivityParams({
                wacc: data.valuation.wacc,
                terminalGrowth: data.valuation.terminalGrowth,
                growthRate: data.valuation.growthRate
            });

        } catch (err) {
            console.error('Analysis error:', err);
            setError('Failed to analyze stock. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value) => {
        if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
        if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
        if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
        return `$${value.toFixed(2)}`;
    };

    // Add the yearlyGrowthRates initialization effect
    useEffect(() => {
        if (!analysis && ticker) {
            handleAnalyze();
        }
        else if (analysis) {
            // Initialize only if yearlyGrowthRates is empty
            if (yearlyGrowthRates.length === 0) {
                setYearlyGrowthRates(new Array(4).fill(analysis.valuation.growthRate));
            }
        }
    }, [analysis, ticker, yearlyGrowthRates.length, handleAnalyze]);


    const handleThesisChange = (value) => {
        setAnalysis(prev => ({
            ...prev,
            analystThesis: value
        }));
    };

    const MetricCard = ({ label, value, prefix = '', suffix = '', trend = null, description = '' }) => (
        <Card className="hover:shadow-lg transition-shadow">
            <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{label}</span>
                        {description && (
                            <div className="relative group">
                                <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                <div className="hidden group-hover:block absolute z-50 w-48 p-2 bg-white border rounded shadow-lg text-xs">
                                    {description}
                                </div>
                            </div>
                        )}
                    </div>
                    {trend !== null && (
                        <div className={`flex items-center ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {trend >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                            <span className="text-sm ml-1">{Math.abs(trend).toFixed(1)}%</span>
                        </div>
                    )}
                </div>
                <div className="text-2xl font-bold">
                    {prefix}{typeof value === 'number' ? value.toFixed(2) : value}{suffix}
                </div>
            </CardContent>
        </Card>
    );

    const SensitivityControl = ({ label, value, onChange, min, max, step, description }) => (
        <div className="space-y-2">
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{label}</span>
                    {description && (
                        <div className="relative group">
                            <Info className="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help" />
                            <div className="hidden group-hover:block absolute z-50 w-48 p-2 bg-white border rounded shadow-lg text-xs">
                                {description}
                            </div>
                        </div>
                    )}
                </div>
                <span className="text-sm font-bold">{value.toFixed(1)}%</span>
            </div>
            <Slider
                value={[value]}
                onValueChange={([newValue]) => onChange(newValue)}
                min={min}
                max={max}
                step={step}
                className="w-full"
            />
        </div>
    );

    return (
        <>
            {analysis && (
                <Header
                    companyName={analysis?.companyInfo?.name}
                    ticker={analysis?.companyInfo?.ticker}
                    isAnalyzing={loading}
                    onSave={handleSaveAnalysis}
                    isAuthenticated={isAuthenticated} // Set this based on your auth state
                    analysis={analysis}
                    yearlyGrowthRates={yearlyGrowthRates}
                    sensitivityParams={sensitivityParams}
                    setError={setError}
                />
            )}
            <div className="p-6 max-w-7xl mx-auto">
                {/* Search Bar Section */}
                <div className="sticky top-0 z-10 bg-white border-b pb-2">
                    <div className="flex gap-4">
                        <Input
                            placeholder="Enter stock ticker (e.g., AAPL)"
                            value={ticker}
                            onChange={(e) => setTicker(e.target.value.toUpperCase())}
                            className="flex-1"
                        />
                        <Button onClick={handleAnalyze} disabled={loading}>
                            {loading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Analyzing...
                                </>
                            ) : (
                                'Analyze Stock'
                            )}
                        </Button>
                    </div>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {analysis && (
                    <>
                        {/* Overview Section */}
                        <section className="pt-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>{analysis.companyInfo.name} ({analysis.companyInfo.ticker})</CardTitle>
                                    <CardDescription>{analysis.companyInfo.sector} | {analysis.companyInfo.industry}</CardDescription>
                                    <div className="border-t pt-4">
                                        <p className="text-sm text-gray-600 leading-relaxed">
                                            {analysis.companyInfo.description}
                                        </p>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-3 gap-4">
                                        <MetricCard
                                            label="Fair Value"
                                            value={analysis.valuation.fairValue}
                                            prefix="$"
                                            description="Estimated intrinsic value based on DCF analysis"
                                        />
                                        <MetricCard
                                            label="Current Price"
                                            value={analysis.valuation.currentPrice}
                                            prefix="$"
                                        />
                                        <MetricCard
                                            label="Margin of Safety"
                                            value={analysis.valuation.marginOfSafety}
                                            suffix="%"
                                            trend={analysis.valuation.marginOfSafety}
                                        />
                                    </div>

                                </CardContent>
                            </Card>
                        </section>
                        {/* Investment Thesis Section - Moved here */}
                        <section className="pt-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Investment Thesis</CardTitle>
                                    <CardDescription>Share your analysis, insights, and recommendations</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <textarea
                                        className="w-full p-4 border rounded-md min-h-[300px] font-mono text-sm"
                                        placeholder="Enter your investment thesis, including any key points about the company's business model, competitive advantages, risks, growth opportunities, and overall investment recommendation..."
                                        value={analysis.analystThesis}
                                        onChange={(e) => handleThesisChange(e.target.value)}
                                    />
                                </CardContent>
                            </Card>
                        </section>
                        {/* Historical FCF Section */}
                        <section className="pt-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Historical Cash Flow Analysis</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="h-80">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={analysis.fcfAnalysis}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="year" />
                                                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                                <Legend />
                                                <Bar dataKey="fcf" name="Free Cash Flow" fill="#2563eb" />
                                                <Bar dataKey="ocf" name="Operating Cash Flow" fill="#4ade80" />
                                                <Bar dataKey="capex" name="Capital Expenditure" fill="#f87171" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </CardContent>
                            </Card>
                        </section>

                        {/* Valuation Section */}
                        <section className="pt-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>DCF Valuation Analysis</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {/* Key Parameters */}
                                    <div className="grid grid-cols-1 gap-4 mb-6">
                                        <Card>
                                            <CardHeader>
                                                <CardTitle>Valuation Parameters</CardTitle>
                                            </CardHeader>
                                            <CardContent className="space-y-4">
                                                {/* Historical FCF Growth - Read only */}
                                                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                                                    <label className="text-sm font-medium text-gray-600">Historical FCF Growth</label>
                                                    <span className="text-sm font-bold">{analysis.valuation.growthRate.toFixed(1)}%</span>
                                                </div>

                                                {/* WACC Control */}
                                                <div>
                                                    <div className="flex justify-between items-center mb-2">
                                                        <label className="text-sm font-medium text-gray-600">WACC</label>
                                                        <span className="text-sm font-bold">{sensitivityParams.wacc.toFixed(1)}%</span>
                                                    </div>
                                                    <Slider
                                                        value={[sensitivityParams.wacc]}
                                                        onValueChange={([newValue]) => handleWACCChange(newValue)}
                                                        min={5}
                                                        max={15}
                                                        step={0.1}
                                                        className="w-full"
                                                    />
                                                </div>

                                                {/* Terminal Growth Control */}
                                                <div>
                                                    <div className="flex justify-between items-center mb-2">
                                                        <label className="text-sm font-medium text-gray-600">Terminal Growth</label>
                                                        <span className="text-sm font-bold">{sensitivityParams.terminalGrowth.toFixed(1)}%</span>
                                                    </div>
                                                    <Slider
                                                        value={[sensitivityParams.terminalGrowth]}
                                                        onValueChange={([newValue]) => handleTerminalGrowthChange(newValue)}
                                                        min={0}
                                                        max={sensitivityParams.wacc - 0.5}
                                                        step={0.1}
                                                        className="w-full"
                                                    />
                                                </div>

                                                {/* Info text */}
                                                <div className="text-xs text-gray-500 mt-2">
                                                    Terminal Growth Rate cannot exceed (WACC - 0.5)%
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>

                                    {/* DCF Table */}
                                    <div className="overflow-x-auto">
                                        <table className="w-full border-collapse text-sm">
                                            <thead>
                                                <tr>
                                                    <th className="border p-2 bg-gray-50 text-left">Year</th>
                                                    <th className="border p-2 bg-gray-50 text-right">Growth Rate</th>
                                                    <th className="border p-2 bg-gray-50 text-right">Projected FCF</th>
                                                    <th className="border p-2 bg-gray-50 text-right">Discount Factor</th>
                                                    <th className="border p-2 bg-gray-50 text-right">Present Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {[1, 2, 3, 4].map(year => {
                                                    const baseFcf = analysis.fcfAnalysis[0].fcf;
                                                    let projectedFcf = baseFcf;

                                                    // Calculate cumulative growth using yearlyGrowthRates
                                                    for (let i = 0; i < year; i++) {
                                                        projectedFcf *= (1 + yearlyGrowthRates[i] / 100);
                                                    }

                                                    const wacc = sensitivityParams.wacc / 100;
                                                    const discountFactor = Math.pow(1 + wacc, year);
                                                    const pv = projectedFcf / discountFactor;

                                                    return (
                                                        <tr key={year} className="hover:bg-gray-50">
                                                            <td className="border p-2">{year}</td>
                                                            <td className="border p-2">
                                                                <div className="flex justify-end items-center">
                                                                    <Input
                                                                        type="number"
                                                                        value={yearlyGrowthRates[year - 1]}
                                                                        onChange={(e) => handleGrowthRateChange(year, e.target.value)}
                                                                        className="w-24 text-right"
                                                                        step="0.1"
                                                                    />
                                                                    <span className="ml-1">%</span>
                                                                </div>
                                                            </td>
                                                            <td className="border p-2 text-right">
                                                                {formatCurrency(projectedFcf)}
                                                            </td>
                                                            <td className="border p-2 text-right">{discountFactor.toFixed(3)}</td>
                                                            <td className="border p-2 text-right">{formatCurrency(pv)}</td>
                                                        </tr>
                                                    );
                                                })}
                                                <tr className="bg-gray-50 font-medium">
                                                    <td className="border p-2">Terminal Value</td>
                                                    <td className="border p-2 text-right">
                                                        {sensitivityParams.terminalGrowth}% perpetual growth
                                                    </td>
                                                    <td className="border p-2 text-right" colSpan={2}>
                                                        {Math.pow(1 + sensitivityParams.wacc / 100, 5).toFixed(3)}
                                                    </td>
                                                    <td className="border p-2 text-right">
                                                        {formatCurrency(analysis.valuation.enterpriseValue * 0.7)}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="mt-6 grid grid-cols-2 gap-4">
                                        <Card>
                                            <CardHeader>
                                                <CardTitle>Enterprise Value Components</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <div className="space-y-4">
                                                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                                                        <span>Enterprise Value</span>
                                                        <span>{formatCurrency(analysis.valuation.enterpriseValue)}</span>
                                                    </div>
                                                    <div className="flex justify-between p-2 bg-gray-50 rounded">
                                                        <span>Shares Outstanding</span>
                                                        <span>{formatCurrency(analysis.valuation.sharesOutstanding)}</span>
                                                    </div>
                                                    <div className="flex justify-between p-2 bg-blue-50 rounded font-bold">
                                                        <span>Fair Value per Share</span>
                                                        <span>${analysis.valuation.fairValue.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </div>
                                </CardContent>
                            </Card>
                        </section>

                        {/* Sensitivity Analysis */}
                        <section className="pt-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Sensitivity Analysis</CardTitle>
                                    <CardDescription>Adjust parameters to see how they affect the valuation</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    {(() => {
                                        // Safety check for yearlyGrowthRates
                                        if (!yearlyGrowthRates || yearlyGrowthRates.length === 0) {
                                            return <div>Loading growth rates...</div>;
                                        }

                                        const baseGrowthRate = yearlyGrowthRates[0];

                                        // Create arrays for WACC and growth rates
                                        const waccValues = [
                                            Number(sensitivityParams.wacc) - 2,
                                            Number(sensitivityParams.wacc),
                                            Number(sensitivityParams.wacc) + 2
                                        ];

                                        console.log('Generated WACC values:', waccValues);


                                        const growthValues = [
                                            baseGrowthRate - 2,
                                            baseGrowthRate,
                                            baseGrowthRate + 2
                                        ];

                                        // Helper function to calculate valuation with given parameters
                                        const calculateSensitivityValue = (waccRate, baseGrowthRate) => {
                                            const growthRateAdjustment = baseGrowthRate - yearlyGrowthRates[0];
                                            const adjustedGrowthRates = yearlyGrowthRates.map(rate => rate + growthRateAdjustment);

                                            // Use the current terminal growth rate for sensitivity calculations
                                            const result = calculateValuation(adjustedGrowthRates, waccRate, sensitivityParams.terminalGrowth);

                                            return {
                                                enterpriseValue: result.enterpriseValue,
                                                sharePrice: result.fairValue
                                            };
                                        };

                                        return (
                                            <div className="space-y-6">


                                                {/* Enterprise Value Matrix */}
                                                <div className="mt-6">
                                                    <h3 className="text-lg font-medium mb-4">Enterprise Value Sensitivity Matrix</h3>
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full border-collapse text-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th className="border p-2 bg-gray-50">WACC ↓ Growth →</th>
                                                                    {growthValues.map((growth) => (
                                                                        <th key={growth} className="border p-2 bg-gray-50 text-right">
                                                                            {growth.toFixed(1)}%
                                                                        </th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {waccValues.map((wacc) => (
                                                                    <tr key={wacc}>
                                                                        <td className="border p-2 bg-gray-50">{wacc.toFixed(1)}%</td>
                                                                        {growthValues.map((growth) => {
                                                                            console.log('Calculating for WACC:', wacc, 'Growth:', growth);

                                                                            const { enterpriseValue } = calculateSensitivityValue(wacc, growth);
                                                                            const variance = ((enterpriseValue - analysis.valuation.enterpriseValue) / analysis.valuation.enterpriseValue) * 100;

                                                                            return (
                                                                                <td key={`${wacc}-${growth}`}
                                                                                    className={`border p-2 text-right ${variance > 10 ? 'bg-green-50' : variance < -10 ? 'bg-red-50' : ''}`}>
                                                                                    {formatCurrency(enterpriseValue)}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                {/* Share Price Matrix */}
                                                <div className="mt-6">
                                                    <h3 className="text-lg font-medium mb-4">Share Price Sensitivity Matrix</h3>
                                                    <div className="overflow-x-auto">
                                                        <table className="w-full border-collapse text-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th className="border p-2 bg-gray-50">WACC ↓ Growth →</th>
                                                                    {growthValues.map((growth) => (
                                                                        <th key={growth} className="border p-2 bg-gray-50 text-right">
                                                                            {growth.toFixed(1)}%
                                                                        </th>
                                                                    ))}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {waccValues.map((wacc) => (
                                                                    <tr key={wacc}>
                                                                        <td className="border p-2 bg-gray-50">{wacc.toFixed(1)}%</td>
                                                                        {growthValues.map((growth) => {
                                                                            const { sharePrice } = calculateSensitivityValue(wacc, growth);
                                                                            const variance = ((sharePrice - analysis.valuation.fairValue) / analysis.valuation.fairValue) * 100;

                                                                            return (
                                                                                <td key={`${wacc}-${growth}`}
                                                                                    className={`border p-2 text-right ${variance > 10 ? 'bg-green-50' : variance < -10 ? 'bg-red-50' : ''}`}>
                                                                                    ${sharePrice.toFixed(2)}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </CardContent>
                            </Card>
                        </section>
                    </>
                )}
            </div>

            {/* Auth Dialog */}


            {/* Activity Feed in right margin
            <div className="fixed right-4 top-4 w-80">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Recent Activity
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                            {[...Array(10)].map((_, index) => {
                                const mockActivity = {
                                    ticker: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'META'][index % 5],
                                    fairValue: [180.50, 390.20, 142.30, 145.60, 420.30][index % 5],
                                    currentPrice: [170.20, 405.10, 138.90, 150.20, 410.50][index % 5],
                                    timestamp: `${index + 1}m ago`,
                                    user: `User${index + 1}`
                                };

                                return (
                                    <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded mb-2">
                                        <div>
                                            <div className="font-medium">{mockActivity.ticker}</div>
                                            <div className="text-xs text-gray-500">by {mockActivity.user}</div>
                                            <div className="text-xs text-gray-500">{mockActivity.timestamp}</div>
                                        </div>
                                        <div className="text-right">
                                            <div className={mockActivity.fairValue > mockActivity.currentPrice ? 'text-green-500' : 'text-red-500'}>
                                                ${mockActivity.fairValue.toFixed(2)}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                Current: ${mockActivity.currentPrice.toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            </div> */}
        </>
    );
};

export default EnhancedValueDashboard;